import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTools } from '../hooks/useTools';
import { createToolSlug } from '../pages/ToolDetailPage';
// Tool logo component with fallback handling
function ToolLogo({
  tool
}: {
  tool: any;
}) {
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const domain = tool.website_url ? new URL(tool.website_url).hostname : '';
  // Multiple logo sources as fallbacks (same as ToolCard)
  const logoSources = [tool.logo_url, `https://logo.clearbit.com/${domain}`, `https://www.google.com/s2/favicons?domain=${domain}&sz=128`, `https://icons.duckduckgo.com/ip3/${domain}.ico`].filter(Boolean);
  const currentLogoUrl = logoSources[fallbackIndex];
  const handleLogoError = () => {
    // Try next fallback source
    if (fallbackIndex < logoSources.length - 1) {
      setFallbackIndex(fallbackIndex + 1);
    } else {
      // All sources failed, show emoji
      setLogoError(true);
    }
  };
  // Get category emoji
  const getCategoryEmoji = (category: string) => {
    const emojiMap: Record<string, string> = {
      Image: '🎨',
      Video: '🎬',
      Audio: '🎵',
      Development: '💻',
      Writing: '✍️',
      Design: '🎨',
      Productivity: '⚡',
      Research: '🔬',
      Other: '🔧'
    };
    return emojiMap[category] || '🔧';
  };
  return <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0" data-id="element-349">
      {!logoError && currentLogoUrl ? <img src={currentLogoUrl} alt={tool.name} className="w-full h-full object-contain p-1" onError={handleLogoError} data-id="element-350" /> : <span className="text-xl" data-id="element-351">{getCategoryEmoji(tool.category)}</span>}
    </div>;
}
export function SearchBar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const {
    tools
  } = useTools();
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Filter tools based on search query
  const getSuggestions = () => {
    if (searchQuery.length < 3) return [];
    const query = searchQuery.toLowerCase();
    return tools.filter(tool => {
      const nameMatch = tool.name.toLowerCase().includes(query);
      const categoryMatch = tool.category.toLowerCase().includes(query);
      const descriptionMatch = tool.description.toLowerCase().includes(query);
      return nameMatch || categoryMatch || descriptionMatch;
    }).sort((a, b) => {
      const aNameMatch = a.name.toLowerCase().includes(query);
      const bNameMatch = b.name.toLowerCase().includes(query);
      const aCategoryMatch = a.category.toLowerCase().includes(query);
      const bCategoryMatch = b.category.toLowerCase().includes(query);
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      if (aCategoryMatch && !bCategoryMatch) return -1;
      if (!aCategoryMatch && bCategoryMatch) return 1;
      return 0;
    }).slice(0, 5);
  };
  const suggestions = getSuggestions();
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
    setShowSuggestions(false);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(value.length >= 3);
  };
  const handleSuggestionClick = () => {
    setShowSuggestions(false);
    setSearchQuery('');
  };
  return <section className="py-12 px-4 max-w-4xl mx-auto" data-id="element-352">
      <motion.form initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} onSubmit={handleSearch} className="relative" ref={searchRef} data-id="element-353">
        {/* Search bar container - minimal design */}
        <div className={`relative flex items-center bg-white/5 backdrop-blur-sm rounded-xl border transition-all ${isFocused ? 'border-cyan-500/50 bg-white/10' : 'border-white/10'}`} data-id="element-354">
          {/* Search icon */}
          <div className="pl-6 pr-4 flex items-center" data-id="element-355">
            <Search className={`transition-colors ${isFocused ? 'text-cyan-400' : 'text-gray-400'}`} size={22} data-id="element-356" />
          </div>

          {/* Input field */}
          <input type="text" value={searchQuery} onChange={handleInputChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="Search for AI tools... (e.g., 'image generator', 'code assistant')" className="flex-1 bg-transparent border-none focus:ring-0 text-white placeholder-gray-500 text-base py-4 pr-4 focus:outline-none" data-id="element-357" />

          {/* Search button with gradient and white text */}
          <button type="submit" className="m-2 px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-500 hover:to-purple-500 text-white font-bold transition-all flex items-center gap-2 shrink-0" data-id="element-358">
            <Sparkles size={16} data-id="element-359" />
            <span data-id="element-360">Search</span>
          </button>
        </div>

        {/* Search Suggestions Dropdown */}
        <AnimatePresence data-id="element-361">
          {showSuggestions && suggestions.length > 0 && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="absolute top-full left-0 right-0 mt-2 bg-black/90 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50" data-id="element-362">
              <div className="p-2" data-id="element-363">
                <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-364">
                  Suggested Tools ({suggestions.length})
                </div>
                <div className="space-y-1" data-id="element-365">
                  {suggestions.map(tool => <Link key={tool.id} to={`/${createToolSlug(tool.name)}`} onClick={handleSuggestionClick} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-all group" data-id="element-366">
                      {/* Tool Logo with fallback handling */}
                      <ToolLogo tool={tool} data-id="element-367" />

                      {/* Tool Info */}
                      <div className="flex-1 min-w-0" data-id="element-368">
                        <div className="flex items-center gap-2 mb-1" data-id="element-369">
                          <h4 className="text-white font-medium group-hover:text-cyan-400 transition-colors truncate" data-id="element-370">
                            {tool.name}
                          </h4>
                          <span className={`
                            px-2 py-0.5 rounded text-xs font-medium border shrink-0
                            ${tool.category === 'Image' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : tool.category === 'Video' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : tool.category === 'Audio' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : tool.category === 'Development' ? 'bg-green-500/10 text-green-400 border-green-500/20' : tool.category === 'Writing' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-white/5 text-gray-400 border-white/10'}
                          `} data-id="element-371">
                            {tool.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 truncate" data-id="element-372">
                          {tool.description}
                        </p>
                      </div>
                    </Link>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Popular searches */}
        <div className="mt-4 flex items-center gap-3 flex-wrap justify-center" data-id="element-373">
          <span className="text-xs text-gray-500" data-id="element-374">Popular:</span>
          {['Image', 'Code', 'Video', 'Writing'].map(term => <button key={term} type="button" onClick={() => {
          setSearchQuery(term);
          setShowSuggestions(term.length >= 3);
        }} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-cyan-500/30 hover:bg-white/10 transition-all" data-id="element-375">
              {term}
            </button>)}
        </div>
      </motion.form>
    </section>;
}